import * as github from '@actions/github';
import * as core from '@actions/core';
import * as fs from 'node:fs';
import * as path from 'node:path';
import { ByteBudget, snapshotBudgetCharge, writeBudgetedUtf8File } from './byte-budget';
import { prepareExternalSources } from './external-sources';
import { ExternalSourceRule, rulesForBaseRepository } from './external-source-rules';
import { looksLikeText, safeSnapshotPath } from './utils';

type Octokit = ReturnType<typeof github.getOctokit>;

interface IssueComment {
  id?: number;
  body?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface ChangedFile {
  filename?: string | null;
  status?: string | null;
  additions?: number;
  deletions?: number;
  changes?: number;
  previous_filename?: string | null;
  sha?: string | null;
  patch?: string | null;
}

export interface PullRequestSnapshot {
  number: number;
  title: string;
  author: string;
  body: string;
  draft: boolean;
  state: string;
  base: {
    repo: string;
    cloneUrl: string;
    ref: string;
    sha: string;
  };
  head: {
    repo: string;
    owner: string;
    repoName: string;
    ref: string;
    sha: string;
  };
}

interface PrepareReviewInputsOptions {
  octokit: Octokit;
  owner: string;
  repo: string;
  workspace: string;
  reviewDir: string;
  pullRequest: PullRequestSnapshot;
  includeRebuttal: boolean;
  maxSnapshotBytes: number;
  maxTotalSnapshotBytes: number;
  metadataBudget: ByteBudget;
}

export async function prepareReviewInputs(options: PrepareReviewInputsOptions): Promise<void> {
  const workspaceDir = fs.realpathSync(options.workspace);
  const reviewDir = fs.realpathSync(path.resolve(workspaceDir, options.reviewDir));
  const filesDir = path.join(reviewDir, 'changed-files');
  const filesDirResolved = path.resolve(filesDir);
  const externalSourcesDir = path.join(reviewDir, 'external-sources');
  const externalSourcesDirResolved = path.resolve(externalSourcesDir);
  const reviewBundlePath = path.relative(workspaceDir, reviewDir).split(path.sep).join('/');
  const snapshotBudget = new ByteBudget(options.maxTotalSnapshotBytes);
  const detectionPatchBudget = new ByteBudget(Math.min(
    options.maxTotalSnapshotBytes,
    options.metadataBudget.limitBytes,
  ));
  const externalSourceRules = rulesForBaseRepository(`${options.owner}/${options.repo}`);

  fs.mkdirSync(filesDir, { recursive: true });

  const pr = options.pullRequest;
  const prBody = [
    '# Pull Request Metadata',
    '',
    `Number: #${pr.number}`,
    `Title: ${pr.title}`,
    `Author: ${pr.author}`,
    `Base: ${pr.base.repo}:${pr.base.ref}@${pr.base.sha}`,
    `Head: ${pr.head.repo}:${pr.head.ref}@${pr.head.sha}`,
    '',
    '## Pull Request Body',
    '',
    pr.body || '(empty)',
    '',
  ].join('\n');
  writeReviewMetadata(options, reviewDir, 'pr-body.md', prBody, 'Pull request body metadata');

  const rebuttals: IssueComment[] = [];
  if (options.includeRebuttal) {
    for await (const response of options.octokit.paginate.iterator(options.octokit.rest.issues.listComments, {
      owner: options.owner,
      repo: options.repo,
      issue_number: pr.number,
      per_page: 100,
    })) {
      for (const comment of response.data as unknown as IssueComment[]) {
        if (isRebuttalCommand(comment.body)) {
          rebuttals.push({
            id: comment.id,
            body: comment.body,
            created_at: comment.created_at,
            updated_at: comment.updated_at,
          });
        }
      }
    }
  }

  const latestRebuttal = selectLatestRebuttalComment(rebuttals);
  writeReviewMetadata(
    options,
    reviewDir,
    'rebuttal-comment.json',
    `${JSON.stringify({ rebuttal_comment: latestRebuttal ? {
      comment_id: latestRebuttal.id || null,
      rebuttal: rebuttalTextFromBody(latestRebuttal.body || ''),
    } : null }, null, 2)}\n`,
    'Pull request rebuttal metadata',
  );

  const changedFiles: ChangedFile[] = [];
  for await (const response of options.octokit.paginate.iterator(options.octokit.rest.pulls.listFiles, {
    owner: options.owner,
    repo: options.repo,
    pull_number: pr.number,
    per_page: 100,
  })) {
    for (const file of response.data as unknown as ChangedFile[]) {
      const filename = String(file.filename || '');
      const detectionPatch = isExternalDetectionFile(filename, externalSourceRules)
        && typeof file.patch === 'string'
        && detectionPatchBudget.tryReserve(Buffer.byteLength(file.patch, 'utf8'))
        ? file.patch
        : undefined;
      changedFiles.push({
        filename,
        status: file.status,
        additions: file.additions,
        deletions: file.deletions,
        changes: file.changes,
        previous_filename: file.previous_filename,
        sha: file.sha,
        patch: detectionPatch,
      });
    }
  }
  const changedFilesMetadata = changedFiles.map((file) => ({
    filename: file.filename,
    status: file.status,
    additions: file.additions,
    deletions: file.deletions,
    changes: file.changes,
    previous_filename: file.previous_filename || null,
    sha: file.sha || null,
  }));
  writeReviewMetadata(
    options,
    reviewDir,
    'changed-files.json',
    `${JSON.stringify(changedFilesMetadata, null, 2)}\n`,
    'Changed-file metadata',
  );

  const manifest = [
    '# Changed File Snapshots',
    '',
    `Head repository: ${pr.head.repo}`,
    `Head SHA: ${pr.head.sha}`,
    '',
    `Review PR changes from \`${reviewBundlePath}/pr.diff\` plus the inert PR-head file snapshots listed below. The workspace may contain GitHub's synthesized PR merge tree or an exact base checkout, depending on the caller workflow.`,
    '',
  ];
  const changedFileTexts = new Map<string, string>();
  let aggregateBudgetSkippedFiles = 0;

  const orderedChangedFiles = [...changedFiles].sort((left, right) => {
    const leftDetection = isExternalDetectionFile(String(left.filename || ''), externalSourceRules);
    const rightDetection = isExternalDetectionFile(String(right.filename || ''), externalSourceRules);
    return Number(rightDetection) - Number(leftDetection)
      || String(left.filename || '').localeCompare(String(right.filename || ''));
  });
  for (const file of orderedChangedFiles) {
    const filename = String(file.filename || '');
    if (
      file.status !== 'removed'
      && file.sha
      && snapshotBudget.remainingBytes < snapshotBudgetCharge(0)
    ) {
      aggregateBudgetSkippedFiles += 1;
      continue;
    }
    const { normalized, target } = safeSnapshotPath({
      filename,
      rootDir: filesDir,
      rootDirResolved: filesDirResolved,
      description: 'changed-file',
    });
    manifest.push(`## ${filename}`);
    manifest.push(`- Status: ${file.status}`);
    manifest.push(`- Additions: ${file.additions}`);
    manifest.push(`- Deletions: ${file.deletions}`);
    manifest.push(`- Changes: ${file.changes}`);
    if (file.previous_filename) {
      manifest.push(`- Previous filename: ${file.previous_filename}`);
    }
    if (file.status === 'removed') {
      manifest.push('- Snapshot: file removed in PR', '');
      continue;
    }
    if (!file.sha) {
      manifest.push('- Snapshot: unavailable because GitHub did not provide a blob SHA', '');
      continue;
    }

    try {
      const { data: blob } = await options.octokit.rest.git.getBlob({
        owner: pr.head.owner,
        repo: pr.head.repoName,
        file_sha: file.sha,
      });
      if (blob.encoding !== 'base64') {
        manifest.push(`- Snapshot: skipped unsupported blob encoding ${blob.encoding}`, '');
        continue;
      }
      const content = Buffer.from(blob.content, 'base64');
      if (content.length > options.maxSnapshotBytes) {
        manifest.push(`- Snapshot: skipped oversized file (${content.length} bytes)`, '');
        continue;
      }
      if (!looksLikeText(content)) {
        manifest.push('- Snapshot: skipped non-text file', '');
        continue;
      }
      const budgetCharge = snapshotBudgetCharge(content.length);
      if (!snapshotBudget.tryReserve(budgetCharge)) {
        manifest.push(`- Snapshot: skipped aggregate snapshot budget (${content.length} payload bytes; ${budgetCharge} charged bytes; ${snapshotBudget.remainingBytes} of ${snapshotBudget.limitBytes} bytes remaining)`, '');
        continue;
      }
      fs.mkdirSync(path.dirname(target), { recursive: true });
      fs.writeFileSync(target, content);
      if (isExternalDetectionFile(normalized, externalSourceRules)) {
        changedFileTexts.set(normalized, content.toString('utf8'));
      }
      manifest.push(`- Snapshot: ${reviewBundlePath}/changed-files/${normalized}`, '');
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      manifest.push(`- Snapshot: unavailable (${singleLine(detail)})`, '');
    }
  }
  if (aggregateBudgetSkippedFiles > 0) {
    manifest.push(
      '## Aggregate snapshot budget omissions',
      '',
      `Skipped ${aggregateBudgetSkippedFiles} changed-file snapshot(s) after less than the minimum per-file disk charge remained. File metadata remains available in \`changed-files.json\` and the exact changes remain available in \`pr.diff\`.`,
      '',
    );
  }

  const externalManifest = await prepareExternalSources({
    octokit: options.octokit,
    coreLogger: core,
    baseRepository: `${options.owner}/${options.repo}`,
    workspaceDir,
    externalSourcesDir,
    externalSourcesDirResolved,
    changedFiles,
    changedFileTexts,
    rules: externalSourceRules,
    maxSnapshotBytes: options.maxSnapshotBytes,
    snapshotBudget,
    reviewBundlePath,
  });
  if (externalManifest.length > 0) {
    manifest.push('## External source snapshots');
    manifest.push(`- Manifest: ${reviewBundlePath}/external-sources-manifest.md`);
    manifest.push('');
  }

  writeReviewMetadata(
    options,
    reviewDir,
    'changed-files-manifest.md',
    `${manifest.join('\n')}\n`,
    'Changed-file manifest',
  );
  writeReviewMetadata(
    options,
    reviewDir,
    'external-sources-manifest.md',
    externalManifest.length > 0
      ? `${externalManifest.join('\n')}\n`
      : '# External Source Snapshots\n\nNo centralized external source snapshot rules matched this PR.\n',
    'External-source manifest',
  );
  core.info(`Prepared Codex review inputs in ${reviewDir}.`);
}

function writeReviewMetadata(
  options: PrepareReviewInputsOptions,
  reviewDir: string,
  filename: string,
  content: string,
  description: string,
): void {
  writeBudgetedUtf8File({
    file: path.join(reviewDir, filename),
    content,
    budget: options.metadataBudget,
    description,
  });
}

function isExternalDetectionFile(filename: string, rules: ExternalSourceRule[]): boolean {
  if (filename === '.github/workflows/go-ci.yml' || filename === 'go.mod' || filename.endsWith('/go.mod')) {
    return true;
  }
  if (filename === 'go.sum' || filename.endsWith('/go.sum')) {
    return true;
  }
  return rules.some((rule) => (
    rule.moduleLockFiles.includes(filename) ||
    rule.markerPrefixes.some((prefix) => pathMatchesPrefix(filename, prefix))
  ));
}

function pathMatchesPrefix(filename: string, prefix: string): boolean {
  if (prefix.endsWith('/')) {
    return filename.startsWith(prefix);
  }
  return filename === prefix || filename.startsWith(`${prefix}/`);
}

function isRebuttalCommand(body: string | null | undefined): boolean {
  return typeof body === 'string' && /^\/rebuttal(?:\s|$)/.test(body);
}

function rebuttalSortKey(comment: IssueComment): number {
  const timestamp = Date.parse(comment.updated_at || comment.created_at || '');
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export function selectLatestRebuttalComment(comments: IssueComment[]): IssueComment | null {
  return comments
    .filter((comment) => isRebuttalCommand(comment.body))
    .sort((left, right) => {
      const timeDelta = rebuttalSortKey(right) - rebuttalSortKey(left);
      if (timeDelta !== 0) {
        return timeDelta;
      }
      return Number(right.id || 0) - Number(left.id || 0);
    })[0] || null;
}

export function rebuttalTextFromBody(body: string): string {
  return String(body || '').replace(/^\/rebuttal(?:\s+|$)/, '').trim();
}

function singleLine(value: string): string {
  return String(value || '').replace(/\s+/g, ' ').trim() || '(unknown)';
}
